import { watch } from 'vue';

import { useSSE } from '@/composables/useSSE';
import { logAppError } from '@/config/sentryConfig';
import dayjs from '@/plugins/dayjs';
import useUserDepotsStore from '@/stores/userDepots';
import { useMockBekkiChatApi } from './bekkiChatMock';
import { parseChatChart } from './chart';
import {
  BEKKI_INACTIVITY_TIMEOUT_MS,
  CHAT_MESSAGES_PATH,
  IS_BEKKI_MOCK
} from './constants';
import { parseChatTable } from './table';
import {
  BEKKI_SERVER_ERROR_CODES,
  type BekkiChatApi,
  type BekkiErrorCode,
  type ChatEvent,
  type ChatRequest,
  THINKING_STEP_IDS,
  THINKING_STEP_STATES
} from './types';

interface ActiveTurn {
  onEvent: (event: ChatEvent) => void;
  resolve: () => void;
  hasContent: boolean;
}

const isOneOf = <T extends string>(
  values: readonly T[],
  value: unknown
): value is T => (values as readonly unknown[]).includes(value);

const isTimestamp = (value: unknown): value is string =>
  typeof value === 'string' && dayjs(value).isValid();

const getHttpStatus = (sseError: string | null): string | undefined =>
  sseError?.match(/HTTP (\d{3})/)?.[1];

const useSseBekkiChatApi = (): BekkiChatApi => {
  const sse = useSSE({
    reconnect: false,
    inactivityTimeout: BEKKI_INACTIVITY_TIMEOUT_MS
  });

  let activeTurn: ActiveTurn | null = null;

  const emitEvent = (event: ChatEvent) => {
    activeTurn?.onEvent(event);
  };

  const emitTerminalEvent = (event: ChatEvent) => {
    const turn = activeTurn;
    if (!turn) return;

    activeTurn = null;
    turn.onEvent(event);
    sse.disconnect();
    turn.resolve();
  };

  const logInvalidPayload = (section: string, raw: string, error: unknown) => {
    logAppError({
      error,
      functionName: 'useBekkiChatApi',
      section,
      level: 'warning',
      metadata: { raw: raw.slice(0, 200) }
    });
  };

  const parsePayload = <T>(raw: string, section: string): T | null => {
    try {
      const payload: unknown = JSON.parse(raw);
      if (typeof payload !== 'object' || payload === null) {
        throw new Error('SSE payload is not an object');
      }
      return payload as T;
    } catch (parseError) {
      logInvalidPayload(section, raw, parseError);
      return null;
    }
  };

  sse.onEvent('start', (raw) => {
    const payload = parsePayload<{ messageId?: string }>(raw, 'start');
    if (!payload) return;

    const { messageId } = payload;
    if (!messageId) {
      logInvalidPayload('start', raw, new Error('start is missing messageId'));
      return;
    }

    emitEvent({ type: 'start', messageId });
  });

  sse.onEvent('step', (raw) => {
    const payload = parsePayload<{ id?: unknown; state?: unknown }>(
      raw,
      'step'
    );
    if (!payload) return;

    const { id, state } = payload;
    if (
      !isOneOf(THINKING_STEP_IDS, id) ||
      !isOneOf(THINKING_STEP_STATES, state)
    ) {
      logInvalidPayload('step', raw, new Error('Unknown step id or state'));
      return;
    }

    emitEvent({ type: 'step', step: { id, state } });
  });

  sse.onEvent('delta', (raw) => {
    const payload = parsePayload<{ text?: string }>(raw, 'delta');
    if (!payload) return;

    if (typeof payload.text !== 'string') {
      logInvalidPayload('delta', raw, new Error('delta is missing text'));
      return;
    }

    if (activeTurn) activeTurn.hasContent = true;
    emitEvent({ type: 'delta', text: payload.text });
  });

  sse.onEvent('chart', (raw) => {
    const payload = parsePayload<unknown>(raw, 'chart');
    if (!payload) return;

    try {
      const chart = parseChatChart(payload);
      if (activeTurn) activeTurn.hasContent = true;
      emitEvent({ type: 'chart', chart });
    } catch (chartError) {
      logInvalidPayload('chart', raw, chartError);
    }
  });

  sse.onEvent('table', (raw) => {
    const payload = parsePayload<unknown>(raw, 'table');
    if (!payload) return;

    try {
      const table = parseChatTable(payload);
      if (activeTurn) activeTurn.hasContent = true;
      emitEvent({ type: 'table', table });
    } catch (tableError) {
      logInvalidPayload('table', raw, tableError);
    }
  });

  sse.onEvent('provenance', (raw) => {
    const payload = parsePayload<{
      dataAsOf?: unknown;
      period?: { from?: unknown; to?: unknown } | null;
      summary?: unknown;
    }>(raw, 'provenance');
    if (!payload) return;

    const { dataAsOf, period, summary } = payload;
    if (!isTimestamp(dataAsOf)) {
      logInvalidPayload(
        'provenance',
        raw,
        new Error('provenance dataAsOf is not a timestamp')
      );
      return;
    }

    const from = period?.from;
    const to = period?.to;

    emitEvent({
      type: 'provenance',
      provenance: {
        dataAsOf,
        ...(isTimestamp(from) && isTimestamp(to)
          ? { period: { from, to } }
          : {}),
        ...(typeof summary === 'string' && summary ? { summary } : {})
      }
    });
  });

  sse.onEvent('done', () => {
    emitTerminalEvent({ type: 'done' });
  });

  sse.onEvent('error', (raw) => {
    const code = parsePayload<{ code?: unknown }>(raw, 'error')?.code;

    emitTerminalEvent({
      type: 'error',
      code: isOneOf(BEKKI_SERVER_ERROR_CODES, code) ? code : 'internal'
    });
  });

  watch(sse.status, (value) => {
    const turn = activeTurn;
    if (!turn) return;
    if (value !== 'ERROR' && value !== 'CLOSED') return;

    const httpStatus = getHttpStatus(sse.error.value);
    let code: BekkiErrorCode;

    if (httpStatus === '429') {
      code = 'rate_limited';
    } else if (httpStatus) {
      code = 'connection';
    } else {
      code = turn.hasContent ? 'generation_stopped' : 'connection';
    }

    emitTerminalEvent({ type: 'error', code });
  });

  const sendMessage = (
    request: ChatRequest,
    onEvent: (event: ChatEvent) => void
  ): Promise<void> =>
    new Promise<void>((resolve) => {
      activeTurn?.resolve();
      activeTurn = { onEvent, resolve, hasContent: false };

      void sse.connect(CHAT_MESSAGES_PATH, { method: 'POST', body: request });
    });

  const abort = () => {
    const turn = activeTurn;
    activeTurn = null;
    sse.disconnect();
    turn?.resolve();
  };

  return { sendMessage, abort };
};

export const useBekkiChatApi = (): BekkiChatApi =>
  IS_BEKKI_MOCK
    ? useMockBekkiChatApi(() => useUserDepotsStore().depots)
    : useSseBekkiChatApi();
